import {
  TextField,
  Button,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  alpha,
  useTheme,
  CircularProgress,
  Alert,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import {
  DeliveryStageToStateList,
  DeliveryState,
  PaymentState,
} from '../../../enums/orders';
import {
  getDeliveryStateColor,
  getDeliveryStateIcon,
  getPaymentStateColor,
  getPaymentStateIcon,
  getPhaseStateColor,
  phaseColors,
} from '../styles/order-states.style';
import DoubleDatePicker from '../double-date-picker/double-date-picker';
import { grey, pink, red, yellow } from '@mui/material/colors';
import GovernorateDistributionWidget from './governorate-distribution-widget/governorate-distribution-widget';
import { WidgetProps } from './home';
import {
  primaryPaletteColor,
  secondaryPaletteColor,
} from '../../../configurations/global.config';
import DeliveredOrdersHistoryWidget from './delivered-orders-history-widget/delivered-orders-history-widget';
import ProfitReport from './profit-report/profit-report';
import { DoubleDate } from '../double-date-picker/double-date-picker.interfaces';
import { OrgSettingsContext } from '../../../contexts/org-settings-context';
import { AlertTriangle } from 'lucide-react';
import { IconWithText } from '../styles/components';
import Alerts from './alerts/alerts';
import { blue, orange, green } from '@mui/material/colors';
import { DoneAll, Replay } from '@mui/icons-material';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { user_is_admin, user_is_org_or_employee } from '../../../utils/permissions';
import { UserType } from '../../../enums/users';

interface CardData {
  label: string;
  value: number;
  icon?: JSX.Element;
  color?: string;
}

interface PhaseStats {
  color: string;
  data: CardData[];
}

enum PHASE {
  AWAITING_ACTION = 'AWAITING ACTION',
  PICKUP = 'PICKUP',
  DELIVERY = 'DELIVERY',
  COMPLETED = 'COMPLETED',
}

export default function DeliveryDashboard({
  stats,
  isLoading,
  errorMessage,
}: WidgetProps): JSX.Element {
  const theme = useTheme();
  const { color } = useContext(OrgSettingsContext);

  const cleanedData: Record<PHASE, PhaseStats> | undefined = stats
    ? {
        'AWAITING ACTION': {
          color: blue[500],
          data: [
            {
              label: DeliveryState.CREATED,
              value: stats.delivery.CREATED,
            },
            {
              label: DeliveryState.PICKUP_REQUESTED,
              value: stats.delivery.PICKUP_REQUESTED,
            },
            {
              label: DeliveryState.AT_WAREHOUSE,
              value: stats.delivery.AT_WAREHOUSE,
            },
            {
              label: DeliveryState.AWAITING_RETURN,
              value: stats.delivery.AWAITING_RETURN,
            },
          ],
        },
        PICKUP: {
          color: orange[500],
          data: [
            {
              label: DeliveryState.PICKUP_ASSIGNED,
              value: stats.delivery.PICKUP_ASSIGNED,
            },
            {
              label: DeliveryState.SCANNED_FOR_PICKUP,
              value: stats.delivery.SCANNED_FOR_PICKUP,
            },
            {
              label: DeliveryState.PICKED_UP,
              value: stats.delivery.PICKED_UP,
            },
          ],
        },
        DELIVERY: {
          color: green[500],
          data: [
            {
              label: DeliveryState.DELIVERY_ASSIGNED,
              value: stats.delivery.DELIVERY_ASSIGNED,
            },
            {
              label: DeliveryState.SCANNED_FOR_DELIVERY,
              value: stats.delivery.SCANNED_FOR_DELIVERY,
            },
            {
              label: DeliveryState.PICKED_UP_FOR_DELIVERY,
              value: stats.delivery.PICKED_UP_FOR_DELIVERY,
            },
            {
              label: DeliveryState.IN_TRANSIT,
              value: stats.delivery.IN_TRANSIT,
            },
          ],
        },
        COMPLETED: {
          color: red[500],
          data: [
            {
              label: 'DELIVERED TO CUSTOMERS',
              value: stats.delivery.DELIVERED.standard,
              color: green[300],
              icon: <DoneAll />,
            },
            {
              label: 'RTO',
              value: stats.delivery.DELIVERED.return,
              color: orange[300],
              icon: <Replay />,
            },
            {
              label: DeliveryState.CANCELLED,
              value: stats.delivery.CANCELLED,
            },
          ],
        },
      }
    : undefined;

  const sumPhaseStats = (phaseStats: PhaseStats) =>
    phaseStats.data.reduce(
      (prevVal, currItem) => prevVal + (currItem.value ?? 0),
      0
    );

  const totalCount = cleanedData
    ? Object.entries(cleanedData).reduce(
        (acc, [_, currStat]) => acc + sumPhaseStats(currStat),
        0
      )
    : 0;

  const { user } = useContext(AuthContext);
  return (
    <Box>
      {' '}
      {isLoading ? (
        <CircularProgress />
      ) : (
        <>
          {stats == undefined ? (
            <Alert severity="error">
              We failed to get data. Please try again later or contact support.
            </Alert>
          ) : (
            <>
              <br />
              <Typography align="center" variant="h4">
                Total Orders: {totalCount}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                {Object.entries(cleanedData!).map(([phase, phaseStats]) => (
                  <Grid item xs={12} md={4} key={phase}>
                    <Box
                      sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: color,
                        overflow: 'hidden',
                        height: '100%', // Ensure full height
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: color,
                          color: 'white',
                          p: 1,
                          textAlign: 'center',
                          flexShrink: 0, // Prevent header from growing
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {phase.replace(/_/g, ' ')} (
                          {sumPhaseStats(phaseStats)})
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 2,
                          flexGrow: 1, // Allow content to grow
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Grid container spacing={1}>
                          {phaseStats.data.map(
                            (stateStat: CardData, index: number) => (
                              <Grid
                                item
                                xs={4}
                                key={stateStat.label}
                                sx={{ display: 'flex' }}
                              >
                                <Box
                                  sx={{
                                    backgroundColor: stateStat.color
                                      ? stateStat.color
                                      : getPhaseStateColor(
                                          phaseStats.color,
                                          index,
                                          phaseStats.data.length
                                        ),
                                    borderRadius: 2,
                                    padding: 1,
                                    flexGrow: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    minHeight: 80, // Minimum height
                                  }}
                                >
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{ fontSize: 11, mt: 0.5 }}
                                    >
                                      {stateStat.label.replace(/_/g, ' ')}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      fontWeight="bold"
                                    >
                                      {stateStat.value || 0}
                                    </Typography>
                                    {stateStat.icon
                                      ? stateStat.icon
                                      : getDeliveryStateIcon(
                                          stateStat.label as DeliveryState
                                        )}
                                  </Box>
                                </Box>
                              </Grid>
                            )
                          )}
                        </Grid>
                      </Box>
                    </Box>
                  </Grid>
                ))}

                <Grid item xs={12} md={4} key={'payment-states'}>
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: color,
                      overflow: 'hidden',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: color,
                        color: 'white',
                        p: 1,
                        textAlign: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        PAYMENT STATES
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Alert severity="info">
                        Cancelled orders are excluded from payment states
                      </Alert>
                      <br />
                      <Grid container spacing={1}>
                        {Array.from(Object.keys(PaymentState)).map(
                          (state: string) => (
                            <Grid
                              item
                              xs={4}
                              key={state}
                              sx={{ display: 'flex' }}
                            >
                              <Box
                                sx={{
                                  backgroundColor: getPaymentStateColor(
                                    state as PaymentState
                                  ),
                                  borderRadius: 2,
                                  padding: 1,
                                  flexGrow: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  textAlign: 'center',
                                  minHeight: 80,
                                }}
                              >
                                {getPaymentStateIcon(state as PaymentState)}

                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: 11, mt: 0.5 }}
                                >
                                  {state.replace(/_/g, ' ')}
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {stats.payment[state as PaymentState] ?? 0}
                                </Typography>
                              </Box>
                            </Grid>
                          )
                        )}
                      </Grid>
                    </Box>
                  </Box>
                </Grid>

                {user_is_org_or_employee(user) && (
                  <Grid item xs={12} md={4} key={'alerts'}>
                    <Box
                      sx={{
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: red[500],
                        overflow: 'hidden',
                        height: '100%', // Ensure full height
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box
                        sx={{
                          backgroundColor: red[500],
                          color: 'white',
                          p: 1,
                          textAlign: 'center',
                          flexShrink: 0, // Prevent header from growing
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          <IconWithText centered>
                            Alerts <AlertTriangle />
                          </IconWithText>
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 2,
                          flexGrow: 1, // Allow content to grow
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Alerts />
                      </Box>
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} md={4} key={'regions'}>
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: color,
                      overflow: 'hidden',
                      height: '100%', // Ensure full height
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: color,
                        color: 'white',
                        p: 1,
                        textAlign: 'center',
                        flexShrink: 0, // Prevent header from growing
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        Regions
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        flexGrow: 1, // Allow content to grow
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <GovernorateDistributionWidget
                        stats={stats}
                        isLoading={isLoading}
                        errorMessage={''}
                      />
                    </Box>
                  </Box>
                </Grid>

                {/* <DeliveredOrdersHistoryWidget */}
                <Grid item xs={12} md={8} key={'history'}>
                  <Box
                    sx={{
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: color,
                      overflow: 'hidden',
                      height: '100%', // Ensure full height
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: color,
                        color: 'white',
                        p: 1,
                        textAlign: 'center',
                        flexShrink: 0, // Prevent header from growing
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        Annual History
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        flexGrow: 1, // Allow content to grow
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <DeliveredOrdersHistoryWidget
                        stats={stats}
                        isLoading={isLoading}
                        errorMessage={''}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}
    </Box>
  );
}
